import { apiClient } from "../lib/apiClient";
import logger from "../utils/logger";

export interface TatoebaSentence {
  id: number | string;
  text: string;
  translations: {
    id: number | string;
    text: string;
    lang: string;
    lang_name?: string;
  }[];
  lang: string;
  lang_name: string;
  isAiGenerated?: boolean;
}

// AI-generated translation format
interface AITranslation {
  id: number | string;
  text: string;
  lang: string;
  lang_name?: string;
}

// Regular Tatoeba translation format
interface TatoebaTranslationGroup {
  length: number;
  [index: number]: TatoebaTranslation;
  forEach: (callback: (item: TatoebaTranslation) => void) => void;
}

interface TatoebaTranslation {
  id: number | string;
  text: string;
  lang: string;
  isDirect: boolean;
}

// Updated interface to match the actual response structure from the AI proxy
interface TatoebaResult {
  id: number | string;
  text: string;
  lang: string;
  lang_name: string;
  translations: Array<{
    id: number | string;
    text: string;
    lang: string;
    lang_name?: string;
  }>;
  isAiGenerated?: boolean;
}

/**
 * Fetches sentences containing a specific word from Tatoeba API
 *
 * @param word The word to search for
 * @param sourceLang The source language code (e.g., 'heb', 'nld')
 * @param targetLangs The target language codes for translations (e.g., 'eng,nld')
 * @param nativeLanguage The user's native language
 * @returns An array of sentences with translations
 */
export async function fetchSentences(
  word: string,
  sourceLang: string,
  targetLangs: string = "eng",
  nativeLanguage: string = "english",
): Promise<TatoebaSentence[]> {
  try {
    // Extract base language code without region (e.g., 'eng' from 'eng-US')
    const baseLanguage = sourceLang.split("-")[0];

    // Map language code to language name for the tatoeba-proxy function
    const langMap: Record<string, string> = {
      eng: "english",
      heb: "hebrew",
      nld: "dutch",
      spa: "spanish",
      fra: "french",
      deu: "german",
      ita: "italian",
      por: "portuguese",
      rus: "russian",
      jpn: "japanese",
      cmn: "chinese",
      kor: "korean",
      ara: "arabic",
    };

    const language = langMap[baseLanguage] || "english";
    logger.log("Fetching sentences for word:", word, "in language:", language);

    // Use apiClient to call our AI endpoint
    let proxyResponse;
    try {
      proxyResponse = await apiClient.generateSentences({
        word,
        language,
        limit: 10,
        targetLangs,
        nativeLanguage,
      });
    } catch (error) {
      logger.error("Error calling generateSentences:", error);
      proxyResponse = null;
    }

    logger.log("AI proxy sentence response:", proxyResponse);

    if (proxyResponse && proxyResponse.results) {
      logger.log("Number of results received:", proxyResponse.results.length);
      // Debug first result structure
      if (proxyResponse.results.length > 0) {
        const firstResult = proxyResponse.results[0];
        logger.log("First result:", firstResult);
        logger.log("First result translations:", firstResult.translations);
      }
    }

    // FOR TESTING: When no results are returned, create some test examples
    if (
      !proxyResponse ||
      !proxyResponse.results ||
      proxyResponse.results.length === 0
    ) {
      logger.log("Creating test sentences because no results were returned");

      // Create some test sentences for different languages
      const testSentences = [];

      if (language === "english") {
        testSentences.push({
          id: "test-1",
          text: `I like to ${word} breakfast with my family.`,
          lang: "eng",
          lang_name: "english",
          translations: [
            {
              id: "trans-1",
              text: `Ik eet graag ${word} met mijn familie.`,
              lang: "nld",
              lang_name: "dutch",
            },
          ],
        });

        testSentences.push({
          id: "test-2",
          text: `She doesn't ${word} meat because she's vegetarian.`,
          lang: "eng",
          lang_name: "english",
          translations: [
            {
              id: "trans-2",
              text: `Ze ${word} geen vlees omdat ze vegetariër is.`,
              lang: "nld",
              lang_name: "dutch",
            },
          ],
        });
      } else if (language === "hebrew") {
        testSentences.push({
          id: "test-h1",
          text: `אני אוהב לאכול ${word} בבוקר.`,
          lang: "heb",
          lang_name: "hebrew",
          translations: [
            {
              id: "trans-h1",
              text: `Ik eet graag ${word} in de ochtend.`,
              lang: "nld",
              lang_name: "dutch",
            },
          ],
        });
      }

      return testSentences;
    }

    // Transform the results to match our expected format
    const sentences: TatoebaSentence[] = [];
    if (
      proxyResponse &&
      proxyResponse.results &&
      proxyResponse.results.length > 0
    ) {
      proxyResponse.results.forEach((result: TatoebaResult) => {
        // Process all sentences with the same format now
        if (result.translations && result.translations.length > 0) {
          sentences.push({
            id: result.id,
            text: result.text,
            translations: result.translations,
            lang: result.lang,
            lang_name: result.lang_name,
            isAiGenerated: result.isAiGenerated,
          });
        }
      });
    }

    logger.log("Processed sentences:", sentences);
    return sentences;
  } catch (error) {
    logger.error("Error fetching sentences from Tatoeba:", error);
    return [];
  }
}

/**
 * Generates formatted sentences for a given word
 *
 * @param word The word to generate sentences for
 * @param language The language of the word
 * @param nativeLanguage The user's native language
 * @returns An array of sentences with translations
 */
export async function generateSentencesForWord(
  word: string,
  language: string,
  nativeLanguage: string = "english",
): Promise<
  {
    sentence: string;
    translation: string;
    translationLang: string;
    sentenceId: number | string;
  }[]
> {
  // Map language to Tatoeba language code
  const langMap: Record<string, { code: string }> = {
    english: { code: "eng" },
    hebrew: { code: "heb" },
    dutch: { code: "nld" },
    spanish: { code: "spa" },
    french: { code: "fra" },
    german: { code: "deu" },
    italian: { code: "ita" },
    portuguese: { code: "por" },
    russian: { code: "rus" },
    japanese: { code: "jpn" },
    chinese: { code: "cmn" },
    korean: { code: "kor" },
    arabic: { code: "ara" },
  };

  const langCode = langMap[language]?.code || "eng";

  // Target languages: native language + English as fallback
  const nativeLangCode = langMap[nativeLanguage]?.code || "eng";
  const targetLangs =
    nativeLangCode === "eng" ? "eng" : `${nativeLangCode},eng`;

  try {
    // For English, we need to set a different target language set
    const actualTargetLangs =
      langCode === "eng" ? "fra,deu,spa,heb,nld" : targetLangs;

    // Fetch sentences from Tatoeba directly
    const tatoebaResults = await fetchSentences(
      word,
      langCode,
      actualTargetLangs,
      nativeLanguage,
    );
    logger.log("Tatoeba results received:", tatoebaResults.length);

    // Format the results
    const formattedResults = tatoebaResults
      .filter((result) => result.translations && result.translations.length > 0)
      .slice(0, 10) // Limit to 10 sentences
      .map((result) => {
        let preferredTranslation;

        // Handle translations differently based on whether they're AI-generated
        if (result.isAiGenerated) {
          // For AI-generated sentences, there is typically only one translation
          preferredTranslation = result.translations[0];
        } else {
          // First try to find a translation in the user's native language
          preferredTranslation = result.translations.find(
            (t) => t.lang === nativeLangCode,
          );

          // If not found, fall back to English
          if (!preferredTranslation && nativeLangCode !== "eng") {
            preferredTranslation = result.translations.find(
              (t) => t.lang === "eng",
            );
          }

          // If still not found, use the first translation
          if (!preferredTranslation && result.translations.length > 0) {
            preferredTranslation = result.translations[0];
          }
        }

        return {
          sentence: result.text,
          translation: preferredTranslation?.text || "",
          translationLang: preferredTranslation?.lang || "eng",
          sentenceId: result.id,
        };
      });

    logger.log("Formatted results:", formattedResults);
    return formattedResults;
  } catch (error) {
    logger.error("Error generating sentences:", error);
    return []; // Return empty array on error
  }
}

/**
 * Stores sentences and their translations in the database
 *
 * @param wordId The ID of the word
 * @param sentences Array of sentences with translations
 * @param userId The user ID
 * @param language The language of the sentences
 */
export async function storeSentencesInDatabase(
  wordId: string,
  sentences: {
    sentence: string;
    translation: string;
    translationLang: string;
    sentenceId: number | string;
  }[],
  userId: string,
  language: string,
): Promise<void> {
  try {
    // Prepare the data for insertion via API
    const sentencesToInsert = sentences.map((s) => ({
      sentence: s.sentence,
      translation: s.translation,
      language: language,
      translationLanguage: s.translationLang,
      wordIds: [wordId],
      // We don't send id, userId, times_seen, remembered etc. as the API handles defaults
      // If we wanted to preserve specific IDs, we'd need to update the API to accept them,
      // but for new sentences, letting the backend/DB handle IDs is better.
    }));

    logger.log("Sentences to insert:", sentencesToInsert);

    await apiClient.createSentences(sentencesToInsert);

    logger.log("Sentences inserted successfully");
  } catch (error) {
    logger.error("Error storing sentences in database:", error);
    throw error;
  }
}

/**
 * Generates lexemes (different word forms) for a given word
 *
 * @param word The word to generate lexemes for
 * @param language The language of the word
 * @returns An array of lexemes
 */
export async function generateLexemes(
  word: string,
  language: string,
): Promise<string[]> {
  try {
    const { lexemes } = await apiClient.generateLexemes(word, language);

    if (lexemes && Array.isArray(lexemes)) {
      return lexemes;
    } else {
      logger.warn("Invalid response format from lexeme generator:", lexemes);
      return [word];
    }
  } catch (error) {
    logger.error("Exception in generateLexemes:", error);
    return [word]; // Return original word on error
  }
}

/**
 * Fetches sentences for all lexemes of a word
 *
 * @param word The word to fetch sentences for
 * @param language The language of the word
 * @param nativeLanguage The user's native language
 * @returns An array of sentences with translations
 */
export async function fetchSentencesForAllLexemes(
  word: string,
  language: string,
  nativeLanguage: string = "english",
): Promise<
  {
    sentence: string;
    translation: string;
    translationLang: string;
    sentenceId: number | string;
  }[]
> {
  try {
    // First get all lexemes for the word
    const lexemes = await generateLexemes(word, language);
    logger.log("Generated lexemes for", word, ":", lexemes);

    // For each lexeme, fetch sentences
    let allSentences: {
      sentence: string;
      translation: string;
      translationLang: string;
      sentenceId: number | string;
    }[] = [];

    for (const lexeme of lexemes) {
      const sentences = await generateSentencesForWord(
        lexeme,
        language,
        nativeLanguage,
      );
      allSentences = [...allSentences, ...sentences];

      // If we have enough sentences, stop fetching more
      if (allSentences.length >= 10) {
        break;
      }
    }

    // Limit to 10 and remove duplicates
    return allSentences
      .filter(
        (s, index, self) =>
          index === self.findIndex((t) => t.sentence === s.sentence),
      )
      .slice(0, 10);
  } catch (error) {
    logger.error("Error fetching sentences for all lexemes:", error);
    return [];
  }
}
